<script setup lang="ts">
import { computed } from 'vue';
import { VChip } from 'vuetify/components';
import { BChip, BTooltip } from '../base';

export type ChipItem = {
    value: string;
    title: string;
    disabled?: boolean;
    color?: string;
    variant?: VChip['$props']['variant'];
    density?: VChip['$props']['density'];
    size?: VChip['$props']['size'];
};

type PropsType = {
    items: ChipItem[];
    modelValue?: string | string[];
    maxVisible?: number;

    // BChipGroup props
    multiple?: boolean;
    mandatory?: boolean;
    column?: boolean;
    filter?: boolean;
    variant?: VChip['$props']['variant'];
    color?: string;
    disabled?: boolean;

    // Stylování počítadla
    counterVariant?: VChip['$props']['variant'];
    counterSize?: VChip['$props']['size'];
    counterColor?: string;
};

const props = withDefaults(defineProps<PropsType>(), {
    maxVisible: 1,
    multiple: false,
    mandatory: false,
    column: false,
    filter: false,
    variant: 'tonal',
    disabled: false,
    counterVariant: 'tonal',
    counterSize: 'small',
    counterColor: 'grey-400',
});

const hasHiddenItems = computed(() => props.items.length > props.maxVisible);
const hiddenCount = computed(() => Math.max(0, props.items.length - props.maxVisible));

const displayedItems = computed(() => {
    if (!hasHiddenItems.value) {
        return props.items;
    }

    return props.items.slice(0, props.maxVisible);
});

const hiddenItems = computed(() => {
    if (!hasHiddenItems.value) {
        return [];
    }

    return props.items.slice(props.maxVisible);
});

defineOptions({ inheritAttrs: false });
</script>

<template>
    <div class="b-collapsible-chip-group">
        <template
            v-for="(item, index) in displayedItems"
            :key="item.value"
        >
            <slot
                name="chip"
                :item="item"
                :index="index"
            >
                <BChip
                    :value="item.value"
                    :disabled="item.disabled"
                    :color="item.color"
                    :variant="item.variant"
                    :size="item.size"
                    :density="item.density"
                >
                    {{ item.title }}
                </BChip>
            </slot>
        </template>

        <BTooltip
            v-if="hasHiddenItems"
            location="bottom start"
            content-class="bg-grey-200"
        >
            <template #activator="{ props: tooltipProps }">
                <BChip
                    v-bind="tooltipProps"
                    :variant="counterVariant"
                    :color="counterColor"
                    :size="counterSize"
                    class="counter-chip"
                >
                    +{{ hiddenCount }}
                </BChip>
            </template>

            <div class="d-flex flex-row ga-1">
                <BChip
                    v-for="item in hiddenItems"
                    :key="item.value"
                    :value="item.value"
                    :disabled="item.disabled"
                    :color="item.color"
                    variant="outlined"
                    :size="item.size"
                >
                    {{ item.title }}
                </BChip>
            </div>
        </BTooltip>
    </div>
</template>

<style scoped>
.b-collapsible-chip-group {
    display: inline-flex;
    align-items: center;
    gap: 2px;
}

.counter-chip {
    cursor: pointer;
    user-select: none;
}

.counter-chip:hover {
    opacity: 0.8;
}
</style>
